/**
 * Projects Page - List and manage construction projects
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Directions as DirectionsIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import projectsService, { ProjectListItem, ProjectSearchParams } from '../services/projects';
import EmptyState from '../components/EmptyState';
import { useAuth } from '../context/AuthContext';
import { useMapView } from '../context/MapViewContext';
import { isSuperAdmin, hasPermission } from '../utils/userPermissions';
import {
  HEADER_CELL_SX,
  ListToolbar,
  ListMap,
  NameCell,
  AddressCell,
  StatusDot,
  RowActionsMenu,
  DetailStrip,
  SelectableTableRow,
  SelectableCard,
  useRowSelection,
  hasCoords,
  directionsUrl,
  type MapPin,
  type RowAction,
} from '../components/ListPageShared';

type StatusFilter = 'all' | 'active' | 'inactive';

const Projects: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = isSuperAdmin(user);
  const canCreate = hasPermission(user, 'projects.create');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Search (debounced — pagination is server-side) and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  // Active/inactive totals for the status switch (respect the search query)
  const [statusCounts, setStatusCounts] = useState({ active: 0, inactive: 0 });

  // Row selection + dialogs
  const selection = useRowSelection();
  const [deleteTarget, setDeleteTarget] = useState<ProjectListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // On mobile the map is opened from the bottom-nav Map button (shared context)
  const mapView = useMapView();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load projects
  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: ProjectSearchParams = {
        project_name: debouncedSearch || undefined,
        is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        page: page + 1, // API pages start at 1
        page_size: pageSize,
      };

      const response = await projectsService.searchProjects(params);
      setProjects(response.items);
      setRowCount(response.total);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, page, pageSize]);

  // Status counts: two page_size-1 queries piggyback on the server total
  const loadStatusCounts = useCallback(async () => {
    try {
      const base = { project_name: debouncedSearch || undefined, page: 1, page_size: 1 };
      const [active, inactive] = await Promise.all([
        projectsService.searchProjects({ ...base, is_active: true }),
        projectsService.searchProjects({ ...base, is_active: false }),
      ]);
      setStatusCounts({ active: active.total, inactive: inactive.total });
    } catch {
      /* switch falls back to stale counts; the list itself reports errors */
    }
  }, [debouncedSearch]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadStatusCounts();
  }, [loadStatusCounts]);

  // Reset to the first page when search or status changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter]);

  const refresh = () => {
    loadProjects();
    loadStatusCounts();
  };

  const handleAdd = () => {
    navigate('/projects/new');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await projectsService.deleteProject(deleteTarget.project_id);
      setSnackbar({ msg: t('projects.deleteSuccess', 'Project deleted successfully'), severity: 'success' });
      setDeleteTarget(null);
      refresh();
    } catch (err: any) {
      setSnackbar({ msg: err.response?.data?.detail || 'Failed to delete project', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (project: ProjectListItem) => {
    const nextActive = !(project.is_active ?? true);
    setTogglingId(project.project_id);
    try {
      await projectsService.setProjectActive(project.project_id, nextActive);
      setSnackbar({
        msg: nextActive
          ? t('projects.activateSuccess', 'Project activated')
          : t('projects.deactivateSuccess', 'Project deactivated'),
        severity: 'success',
      });
      // Re-fetch: under Active/Deactivated filters the row leaves the list
      refresh();
    } catch (err: any) {
      setSnackbar({ msg: err.response?.data?.detail || 'Failed to update project status', severity: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  const formatCreatedDate = (row: ProjectListItem): string => {
    if (!row.date_create) return '—';
    const d = new Date(row.date_create);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const isActive = (project: ProjectListItem) => project.is_active ?? true;
  const isMapped = (project: ProjectListItem) => hasCoords(project.latitude, project.longitude);

  // Pins for the map — projects on the current page with coordinates
  const pins: MapPin[] = useMemo(
    () =>
      projects.filter(isMapped).map((project) => ({
        id: project.project_id,
        position: [Number(project.latitude), Number(project.longitude)] as [number, number],
        popup: (
          <>
            <strong>{project.project_name}</strong>
            <br />
            {project.client && <>{project.client}<br /></>}
            {project.address && <>{project.address}<br /></>}
            <Button
              size="small"
              sx={{ mt: 1, textTransform: 'none', p: 0 }}
              onClick={() => navigate(`/projects/${project.project_id}`)}
            >
              {t('projects.viewProject', 'View project')}
            </Button>
          </>
        ),
      })),
    [projects, navigate, t]
  );

  // -------------------------------------------------------------------------
  // Shared render fragments
  // -------------------------------------------------------------------------

  const editProject = (project: ProjectListItem) => navigate(`/projects/${project.project_id}/edit`);

  const actionsFor = (project: ProjectListItem) => {
    const directions = directionsUrl(project.address, project.latitude, project.longitude);
    const view: RowAction = {
      key: 'view',
      label: t('users.viewDetails', 'View Details'),
      icon: <ViewIcon fontSize="small" />,
      onClick: () => navigate(`/projects/${project.project_id}`),
    };
    const edit: RowAction = {
      key: 'edit',
      label: t('common.edit', 'Edit'),
      icon: <EditIcon fontSize="small" />,
      onClick: () => editProject(project),
    };
    const directionsAction: RowAction | false = !!directions && {
      key: 'directions',
      label: t('listPage.directions', 'Directions'),
      icon: <DirectionsIcon fontSize="small" />,
      href: directions,
    };
    const toggle: RowAction = {
      key: 'toggle',
      label: isActive(project) ? t('projects.deactivate', 'Deactivate') : t('projects.activate', 'Activate'),
      icon: isActive(project) ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />,
      disabled: togglingId === project.project_id,
      onClick: () => handleToggleActive(project),
    };
    const remove: RowAction | false = canDelete && {
      key: 'delete',
      label: t('common.delete', 'Delete'),
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      onClick: () => setDeleteTarget(project),
    };
    return { menu: [view, edit, directionsAction, toggle, remove], strip: [view, edit, directionsAction] };
  };

  const statusDot = (project: ProjectListItem) => (
    <StatusDot
      active={isActive(project)}
      activeLabel={t('projects.statusActive', 'Active')}
      inactiveLabel={t('projects.statusDeactivated', 'Deactivated')}
    />
  );

  // Editing a project is open to every role that can see the list (as before)
  const addressCell = (project: ProjectListItem) => (
    <AddressCell address={project.address} mapped={isMapped(project)} onAdd={() => editProject(project)} />
  );

  const detailStrip = (project: ProjectListItem) => {
    const createdBy = project.create_by
      ? t('projects.createdBy', 'Created {{date}} by {{by}}', { date: formatCreatedDate(project), by: project.create_by })
      : null;
    return (
      <DetailStrip
        description={
          <>
            {project.address?.trim() ||
              t('projects.noAddressHint', 'No address yet. Add one to show this project on the map.')}
            {createdBy && <Typography variant="caption" color="text.disabled" display="block">{createdBy}</Typography>}
          </>
        }
        actions={actionsFor(project).strip}
      />
    );
  };

  const emptyState = (
    <EmptyState
      title={t('projects.noProjects')}
      description={t('projects.noProjectsDescription')}
      icon={<SearchIcon />}
      actionLabel={canCreate ? t('projects.newProject') : undefined}
      onAction={canCreate ? handleAdd : undefined}
    />
  );

  const paginationProps = {
    rowsPerPageOptions: [10, 20, 50, 100],
    component: 'div' as const,
    count: rowCount,
    rowsPerPage: pageSize,
    page,
    onPageChange: (_: unknown, newPage: number) => setPage(newPage),
    onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageSize(parseInt(e.target.value, 10));
      setPage(0);
    },
  };

  if (loading && projects.length === 0 && rowCount === 0 && !error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const filterSummary =
    debouncedSearch || statusFilter !== 'all'
      ? `Showing ${projects.length} of ${rowCount} projects (filtered)`
      : `Total projects: ${rowCount}`;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <ListToolbar
        search={searchQuery}
        onSearch={setSearchQuery}
        searchPlaceholder={t('projects.searchPlaceholder')}
        statusOptions={[
          { key: 'all', label: t('projects.allProjects', 'All Projects'), count: statusCounts.active + statusCounts.inactive },
          { key: 'active', label: t('projects.statusActive', 'Active'), count: statusCounts.active },
          { key: 'inactive', label: t('projects.statusDeactivated', 'Deactivated'), count: statusCounts.inactive },
        ]}
        status={statusFilter}
        onStatus={setStatusFilter}
        onRefresh={refresh}
        loading={loading}
        addLabel={canCreate ? t('projects.newProject') : undefined}
        onAdd={handleAdd}
      />

      <ListMap
        pins={pins}
        selectedId={selection.selectedId}
        onSelect={selection.selectAndReveal}
        total={projects.length}
        missingAddress={projects.filter((p) => !p.address?.trim()).length}
        pinColor="#1565C0"
        selectedPinColor="#0D3C73"
        isMobile={isMobile}
        mobileOpen={mapView.open}
      />

      {/* Mobile card list */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {projects.length === 0 && !loading ? (
            <Paper sx={{ p: 3 }}>{emptyState}</Paper>
          ) : (
            projects.map((project) => (
              <SelectableCard
                key={project.project_id}
                rowRef={selection.rowRef(project.project_id)}
                selected={selection.selectedId === project.project_id}
                onClick={() => selection.toggle(project.project_id)}
                header={
                  <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <NameCell name={project.project_name || '-'} subtitle={project.client} />
                    </Box>
                    {statusDot(project)}
                    <RowActionsMenu actions={actionsFor(project).menu} />
                  </>
                }
                body={addressCell(project)}
                detail={detailStrip(project)}
              />
            ))
          )}
          <TablePagination
            {...paginationProps}
            labelRowsPerPage=""
            sx={{ '& .MuiTablePagination-selectLabel': { display: 'none' } }}
          />
        </Box>
      ) : (
        /* Desktop table: click a row to select it and show its detail strip */
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={HEADER_CELL_SX}>{t('projects.projectName', 'Project')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('projects.address', 'Address')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 130 }}>{t('projects.created', 'Created')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 120 }}>{t('common.status', 'Status')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    {loading ? (
                      <Typography color="text.secondary">{t('common.loading')}</Typography>
                    ) : (
                      emptyState
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                projects.map((project) => (
                  <SelectableTableRow
                    key={project.project_id}
                    rowRef={selection.rowRef(project.project_id)}
                    selected={selection.selectedId === project.project_id}
                    onClick={() => selection.toggle(project.project_id)}
                    colSpan={5}
                    detail={detailStrip(project)}
                  >
                    <TableCell sx={{ maxWidth: 320 }}>
                      <NameCell name={project.project_name || '-'} subtitle={project.client} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>{addressCell(project)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {formatCreatedDate(project)}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusDot(project)}</TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <RowActionsMenu actions={actionsFor(project).menu} />
                    </TableCell>
                  </SelectableTableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* Footer: filter summary on the left, pagination on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filterSummary}
            </Typography>
            <TablePagination {...paginationProps} labelRowsPerPage={t('common.rowsPerPage')} />
          </Box>
        </TableContainer>
      )}


      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>{t('projects.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('projects.deleteWarning')}
            <Box component="ul" sx={{ mb: 1, mt: 1 }}>
              <Box component="li">{t('projects.deleteWarningPours')}</Box>
              <Box component="li">{t('projects.deleteWarningUsers')}</Box>
              <Box component="li">{t('projects.deleteWarningContacts')}</Box>
              <Box component="li">{t('projects.deleteWarningProducts')}</Box>
              <Box component="li">{t('projects.deleteWarningLoggers')}</Box>
            </Box>
          </Alert>
          <Typography>
            {t('projects.deleteConfirm', { name: deleteTarget?.project_name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? t('common.deleting', 'Deleting...') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar !== null} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Projects;
